import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Palmtree, Plus, Trash2 } from "lucide-react";
import api, { handleApiError, formatDate, brTodayStr } from "@/lib/api";
import { toast } from "sonner";

export default function EmployeeVacations({ employeeId, canEdit }) {
  const [vacations, setVacations] = useState([]);
  const [summary, setSummary] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchVacations = useCallback(async () => {
    if (!employeeId) return;
    try {
      const res = await api.get(`/employees/${employeeId}/vacations`);
      setVacations(res.data.vacations || []);
      setSummary(res.data.summary || {});
    } catch (err) { toast.error(handleApiError(err, "Erro ao carregar férias")); }
  }, [employeeId]);

  useEffect(() => { fetchVacations(); }, [fetchVacations]);

  const handleAdd = async () => {
    if (!startDate || !endDate) { toast.error("Preencha as datas"); return; }
    setSaving(true);
    try {
      await api.post(`/employees/${employeeId}/vacations`, { start_date: startDate, end_date: endDate, description });
      toast.success("Férias lançadas");
      setShowForm(false);
      setStartDate(""); setEndDate(""); setDescription("");
      fetchVacations();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao lançar"); }
    setSaving(false);
  };

  const handleDelete = async (vacId) => {
    if (!window.confirm("Tem certeza que deseja remover este registro de férias?")) return;
    try {
      await api.delete(`/employees/${employeeId}/vacations/${vacId}`);
      toast.success("Férias removidas");
      fetchVacations();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  // Ano corrente em Brasília — mesma janela que o backend usa no `summary`
  // de `/employees/{id}/vacations` (`br_now().year`), não o fuso do navegador.
  const currentYear = Number(brTodayStr().slice(0, 4));
  const years = [currentYear, currentYear - 1, currentYear - 2];

  return (
    <div className="space-y-3" data-testid="employee-vacations">
      <Separator className="bg-white/5" />
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2 text-sm font-semibold">
          <Palmtree className="w-4 h-4 text-emerald-400" /> Férias
        </Label>
        {canEdit && (
          <Button variant="ghost" size="sm" onClick={() => setShowForm(!showForm)}
            data-testid="add-vacation-btn" className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground">
            <Plus className="w-3 h-3 mr-1" /> Lançar
          </Button>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-2">
        {years.map(y => (
          <div key={y} className="text-center p-2 rounded-lg bg-black/10 border border-white/5">
            <p className="text-[10px] text-muted-foreground">{y}</p>
            <p className="text-sm font-bold">{summary[String(y)] || 0} <span className="text-[10px] font-normal text-muted-foreground">dias</span></p>
          </div>
        ))}
      </div>

      {/* Add Form */}
      {showForm && (
        <div className="space-y-3 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10 animate-fade-in">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Saída</Label>
              <Input data-testid="vacation-start" type="date" value={startDate}
                onChange={(e) => setStartDate(e.target.value)} className="h-8 bg-black/20 border-white/10 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Retorno</Label>
              <Input data-testid="vacation-end" type="date" value={endDate}
                onChange={(e) => setEndDate(e.target.value)} className="h-8 bg-black/20 border-white/10 text-sm" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Observação (opcional)</Label>
            <Input data-testid="vacation-desc" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: Férias coletivas" className="h-8 bg-black/20 border-white/10 text-sm" />
          </div>
          {startDate && endDate && endDate > startDate && (
            <p className="text-xs text-emerald-400">
              {Math.round((new Date(endDate) - new Date(startDate)) / 86400000)} dias
            </p>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={handleAdd} disabled={saving} data-testid="save-vacation-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7">
              {saving ? <div className="loading-spinner" /> : "Salvar"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowForm(false)} className="text-xs h-7">Cancelar</Button>
          </div>
        </div>
      )}

      {/* List */}
      {vacations.length > 0 && (
        <div className="space-y-1.5">
          {vacations.map(v => (
            <div key={v.id} className="flex items-center justify-between p-2 rounded-lg bg-black/10 border border-white/5 text-xs" data-testid={`vacation-${v.id}`}>
              <div>
                <span className="font-medium">{formatDate(v.start_date)} → {formatDate(v.end_date)}</span>
                <span className="text-muted-foreground ml-2">({v.days} dias)</span>
                {v.description && <span className="text-muted-foreground ml-2">· {v.description}</span>}
              </div>
              {canEdit && (
                <Button variant="ghost" size="icon" onClick={() => handleDelete(v.id)}
                  className="h-6 w-6 text-muted-foreground hover:text-red-400">
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
